import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
  Stack,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { createHrLeaveTransaction } from "../../../api/api";

export default function AdjustBalanceDialog({
  open,
  onClose,
  onSuccess,
  user,
}) {
  const [leaveBucket, setLeaveBucket] = useState("planned");
  const [actionType, setActionType] = useState("credit");
  const [days, setDays] = useState("");
  const [remarks, setRemarks] = useState("Data correction");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!days || Number(days) <= 0) {
      setError("Enter a valid number of days");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await createHrLeaveTransaction({
        user_id: user.user_id,
        leave_bucket: leaveBucket,
        action_type: actionType,
        days: Number(days),
        remarks,
      });

      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err?.errors?.[0] || "Failed to adjust balance");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setLeaveBucket("planned");
    setActionType("credit");
    setDays("");
    setRemarks("Data correction");
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Adjust Leave Balance

        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>

          {/* Captain Details */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700 }}
              >
                Captain Details
              </Typography>

              <Chip
                label={user?.status || "Active"}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>

              {/* Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <PersonIcon
                  fontSize="small"
                  color="action"
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Captain Name
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {user?.name || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Employee Code */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <BadgeIcon
                  fontSize="small"
                  color="action"
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Employee Code
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {user?.emp_code || user?.employee_code || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <EmailIcon
                  fontSize="small"
                  color="action"
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Email
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {user?.email || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Mobile */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <PhoneIcon
                  fontSize="small"
                  color="action"
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Mobile Number
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {user?.mobile_number ||
                      user?.mobile ||
                      user?.phone ||
                      "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Designation */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <BadgeIcon
                  fontSize="small"
                  color="action"
                />

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Designation
                  </Typography>

                  <Typography variant="body2" fontWeight={600}>
                    {user?.designation ||
                      user?.hr_designation ||
                      "Bus Captain"}
                  </Typography>
                </Box>
              </Box>

            </Stack>
          </Box>

          {/* Leave Adjustment */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Leave Adjustment
            </Typography>

            <Stack spacing={2}>

              <ToggleButtonGroup
                value={actionType}
                exclusive
                fullWidth
                size="small"
                onChange={(e, val) =>
                  val && setActionType(val)
                }
              >
                <ToggleButton
                  value="credit"
                  color="success"
                >
                  Credit
                </ToggleButton>

                <ToggleButton
                  value="debit"
                  color="error"
                >
                  Debit
                </ToggleButton>
              </ToggleButtonGroup>

              <TextField
                select
                size="small"
                label="Leave Type"
                value={leaveBucket}
                onChange={(e) =>
                  setLeaveBucket(e.target.value)
                }
                fullWidth
              >
                <MenuItem value="planned">
                  Planned
                </MenuItem>

                <MenuItem value="unplanned">
                  Unplanned
                </MenuItem>
              </TextField>

              <TextField
                size="small"
                label="Days"
                type="number"
                value={days}
                onChange={(e) =>
                  setDays(e.target.value)
                }
                fullWidth
                inputProps={{ min: 0 }}
              />

              <TextField
                size="small"
                label="Remark"
                value={remarks}
                onChange={(e) =>
                  setRemarks(e.target.value)
                }
                placeholder="Data correction"
                fullWidth
              />

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}
            </Stack>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}